import { useEffect, useRef } from "react";

import {
  GoogleAdsConfig,
  resolveInstallConversionTarget,
} from "@src/bootstrap/GoogleAdsConfig";
import { ensureGtag, hasGoogleClickCookie } from "@src/bootstrap/loadGtag";
import {
  clearAdsClickMarker,
  type InstallConversionOrg,
  readAdsClickMarker,
  readReportedOrgIds,
  recordReportedOrgId,
  shouldReportInstallConversion,
} from "@src/utils/installConversion";

export type InstallConversionOutcome = "reported" | "disabled" | "unavailable";

export type InstallConversionReporter = (input: {
  orgId: string;
}) => Promise<InstallConversionOutcome>;

const reportToGoogleAds: InstallConversionReporter = async ({ orgId }) => {
  const sendTo = resolveInstallConversionTarget(GoogleAdsConfig);

  if (!sendTo) {
    return "disabled";
  }

  const loaded = await ensureGtag(GoogleAdsConfig);
  if (!loaded) {
    return "unavailable";
  }

  window.gtag("event", "conversion", {
    send_to: sendTo,
    transaction_id: orgId,
  });

  return "reported";
};

const REPORTERS: InstallConversionReporter[] = [reportToGoogleAds];

export const useInstallConversion = (
  org: InstallConversionOrg | undefined
): void => {
  // Guards against a second run inside the same mount while the first is still
  // awaiting a reporter. The persisted org-id list guards across page loads.
  const inFlight = useRef(false);

  useEffect(() => {
    if (!org || inFlight.current) {
      return;
    }

    const nowTs = Date.now();
    const shouldReport = shouldReportInstallConversion({
      marker: readAdsClickMarker(nowTs),
      hasClickCookie: hasGoogleClickCookie(document.cookie),
      org,
      alreadyReportedOrgIds: readReportedOrgIds(),
      nowTs,
    });
    if (!shouldReport) {
      return;
    }

    inFlight.current = true;

    void (async () => {
      try {
        const outcomes = await Promise.all(
          REPORTERS.map((report) => report({ orgId: org.id }))
        );

        // Something could still have landed. Keep the marker and record
        // nothing, so a later page load can try again.
        if (outcomes.includes("unavailable")) {
          inFlight.current = false;

          return;
        }

        if (!outcomes.includes("reported")) {
          return;
        }

        recordReportedOrgId(org.id);
        clearAdsClickMarker();
      } catch {
        inFlight.current = false;
      }
    })();
  }, [org]);
};
